import type { Asset, AssetQuery, AssetSource, LibraryKey, Paged, Tag, TagGroup, TagGroupDef } from '../types'
import type { AssetRepository } from './assetRepository'
import { getSupabase, STORAGE_BUCKET } from './supabaseClient'
import { signPatiently, SIGNED_URL_TTL_SECONDS } from './signedUrls'
import { thumbLinksFor } from './thumbLinks'
import { NON_PACKAGING_SLUG } from '../data/mockTags'

/**
 * The library, read from Postgres
 * -------------------------------------------------------------
 * Implements the same AssetRepository interface as the mock one, so swapping
 * between them changes nothing above this file.
 *
 * Read-only on purpose. Writing — upload, tagging, the Miro import — comes
 * later; this step exists to prove that real rows and real images reach the
 * screen.
 */

/**
 * The row shapes and the two mappers below are exported so the Worker-backed
 * repository can reuse them. The Worker returns database rows untouched, on
 * purpose: one mapping, in one place. Two copies of it would drift the first
 * time a column was added.
 */

/** Columns, named as the database has them. */
const ASSET_COLUMNS =
  'id, title, original_path, thumbnail_path, source, miro_board_id, source_frame, ' +
  'file_hash, perceptual_hash, width, height, mime_type, ai_description, created_at, ' +
  'asset_tags ( tag_id )'

export interface AssetRow {
  id: string
  title: string | null
  original_path: string
  thumbnail_path: string | null
  source: string | null
  miro_board_id: string | null
  source_frame: string | null
  file_hash: string | null
  perceptual_hash: string | null
  width: number | null
  height: number | null
  mime_type: string | null
  ai_description: string | null
  created_at: string
  asset_tags?: { tag_id: string }[] | null
  /** From a search only (docs/search-ranked.sql). */
  match_rank?: number | null
  /** From a smart search only (docs/smart-search.sql). */
  match_score?: number | null
}

export interface TagRow {
  id: string
  slug: string
  name: string
  group: string
  created_at: string
}

const ASSET_SOURCES: AssetSource[] = ['miro', 'upload', 'generated', 'external']
/** See assetIdsMatchingTagName. */
const TEXT_MATCH_ID_CAP = 400
export /**
 * Note what this does NOT do: it leaves the stored paths as stored paths.
 *
 * It used to turn them into public URLs here, one at a time, which worked
 * only because the bucket was open to the world. Signing is a request, so it
 * happens once for a whole page in `sign()` below rather than once per image.
 */
function toAsset(row: AssetRow): Asset {
  return {
    id: row.id,
    title: row.title ?? '',
    originalPath: row.original_path,
    // Rows without their own thumbnail fall back to the full image; the card
    // measures and scales it, so the grid still looks right.
    thumbnailPath: row.thumbnail_path || row.original_path,
    source: ASSET_SOURCES.includes(row.source as AssetSource)
      ? (row.source as AssetSource)
      : 'external',
    miroBoardId: row.miro_board_id,
    sourceFrame: row.source_frame,
    fileHash: row.file_hash,
    perceptualHash: row.perceptual_hash,
    width: row.width ?? 0,
    height: row.height ?? 0,
    mimeType: row.mime_type ?? 'image/jpeg',
    tagIds: (row.asset_tags ?? []).map((t) => t.tag_id),
    aiDescription: row.ai_description,
    createdAt: row.created_at,
    ...(typeof row.match_rank === 'number' ? { matchRank: row.match_rank } : {}),
    ...(typeof row.match_score === 'number' ? { matchScore: row.match_score } : {}),
  }
}

export function toTag(row: TagRow): Tag {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    // Any category the database holds. Categories are rows, and each library
    // has its own (dw-product, vl-tags); a fixed list here filed every Design
    // Works tag under Audit's Other.
    group: (row.group as TagGroup) || 'other',
  }
}

/**
 * PostgREST parses `or=(...)` itself, so a search word containing a comma,
 * a bracket or a wildcard would break the filter rather than match anything.
 */
function escapeForFilter(word: string): string {
  return word.replace(/[,()*%\\]/g, ' ').trim()
}

const DEADLINE_MS = 30_000

function withDeadline<T>(work: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  return Promise.race([
    work,
    new Promise<T>((_, reject) => {
      timer = setTimeout(() => reject(new Error(message)), ms)
    }),
  ]).finally(() => clearTimeout(timer))
}

export class SupabaseAssetRepository implements AssetRepository {
  private async client() {
    const supabase = getSupabase()
    if (!supabase) throw new Error('Supabase is not configured. See .env.example.')
    return supabase
  }

  /**
   * Exchange the stored paths for links that load.
   *
   * Thumbnails first, from the Worker, which serves them from Cloudflare R2
   * with links that stay the same all day (thumbLinks.ts). Then one request
   * to Supabase for everything else -- the full-size files, and any thumbnail
   * the Worker could not give. The website signs that part itself: it holds a
   * real session, and `storage.objects` lets a signed-in person read.
   *
   * A failure here is not fatal. The rows are still right; the pictures will
   * not draw. Throwing would turn a page of images into an error screen.
   * Paths that did not sign are asked for again (signedUrls.ts, signPatiently).
   */
  private async sign(assets: Asset[]): Promise<Asset[]> {
    const thumbs = await thumbLinksFor(assets)
    const fromSupabase = async (paths: string[]) => {
      const supabase = await this.client()
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrls(paths, SIGNED_URL_TTL_SECONDS)
      if (error) throw new Error(error.message)

      const signed = new Map<string, string>()
      const problems = new Map<string, string>()
      for (const entry of data ?? []) {
        if (entry.path && entry.signedUrl) signed.set(entry.path, entry.signedUrl)
        else if (entry.path) problems.set(entry.path, entry.error ?? 'no link returned')
      }
      return { signed, problems }
    }
    return signPatiently(assets, fromSupabase, thumbs)
  }



  /**
   * One page of the library.
   *
   * Everything happens inside `search_assets`: the tag logic, the packaging
   * switch, word matching across tag names, the count and the cursor. The
   * browser sends the filter and gets back a page — no lists of ids travel in
   * either direction, which is what used to put fifteen kilobytes of UUIDs into
   * a URL and risk the request being refused outright.
   */
  /**
   * A page that never answers (a dropped connection, a stalled sign) used to
   * leave the list on "Loading…" for good (2026-10-13). It now gives up after
   * DEADLINE_MS with an error the page can show, and the next try starts clean.
   */
  async search(query: AssetQuery): Promise<Paged<Asset>> {
    return withDeadline(this.searchNow(query), DEADLINE_MS, 'The library took too long to answer. Try again.')
  }

  private async searchNow(query: AssetQuery): Promise<Paged<Asset>> {
    const supabase = await this.client()
    const { text, tagGroups, packaging = 'all', limit = 60, after = null, library = 'audit', smart = null } = query

    // A sentence understood: scored, best first (docs/smart-search.sql).
    if (smart) {
      const { data: found, error: failed } = await supabase.rpc('search_assets_smart', {
        p_library: library,
        p_tag_ids: smart.tagIds,
        p_words: smart.words,
        p_tag_groups: (tagGroups ?? []).filter((group) => group.length > 0),
        p_packaging: packaging,
        p_limit: limit,
        p_after_score: typeof after?.score === 'number' ? after.score : null,
        p_after_created_at: typeof after?.score === 'number' ? after.createdAt : null,
        p_after_id: typeof after?.score === 'number' ? after.id : null,
        p_tag_weights: smart.tagWeights ?? [],
        p_word_weights: smart.wordWeights ?? [],
        p_core_min: smart.coreMin ?? 0,
        p_tag_aliases: smart.tagAliases ?? [],
        p_phrase: smart.phrase ?? '',
      })
      if (failed) throw new Error(failed.message)
      const rows = (found ?? []) as unknown as (AssetRow & { total_count: number })[]
      const items = await this.sign(rows.map((row) => toAsset(row)))
      const last = rows[rows.length - 1]
      return {
        items,
        total: rows.length > 0 ? Number(rows[0].total_count) : 0,
        next:
          last && rows.length === limit
            ? { createdAt: last.created_at, id: last.id, score: Number(last.match_score) || 0 }
            : null,
      }
    }

    const { data, error } = await supabase.rpc('search_assets', {
      p_library: library,
      p_text: text?.trim() ?? '',
      p_tag_groups: (tagGroups ?? []).filter((group) => group.length > 0),
      p_packaging: packaging,
      p_limit: limit,
      p_after_created_at: after?.createdAt ?? null,
      p_after_id: after?.id ?? null,
      // Only sent when there is one: a database without docs/search-ranked.sql
      // has no such argument, and naming it would fail every search.
      ...(typeof after?.rank === 'number' ? { p_after_rank: after.rank } : {}),
    })
    if (error) throw new Error(error.message)

    // Rows come back without their tags: the function returns one row per
    // image, and joining the tags back on would multiply that by however many
    // each image carries, for data nothing on screen reads. `getById` still
    // brings them, so an asset from a search has an empty `tagIds` while the
    // same asset fetched on its own does not — worth knowing before writing
    // anything that depends on them.
    const rows = (data ?? []) as unknown as (AssetRow & { total_count: number })[]
    const items = await this.sign(rows.map((row) => toAsset(row)))
    const last = rows[rows.length - 1]

    return {
      items,
      // Every row carries the same total; with no rows there is nothing to find.
      total: rows.length > 0 ? Number(rows[0].total_count) : 0,
      // A short page is the last page. Asking for one more just to learn that
      // would double the number of requests an endless list makes.
      next:
        last && rows.length === limit
          ? {
              createdAt: last.created_at,
              id: last.id,
              ...(typeof last.match_rank === 'number' ? { rank: last.match_rank } : {}),
            }
          : null,
    }
  }

  async getById(id: string): Promise<Asset | null> {
    const supabase = await this.client()
    const { data, error } = await supabase
      .from('assets')
      .select(ASSET_COLUMNS)
      .eq('id', id)
      .maybeSingle()
    if (error) throw new Error(error.message)
    if (!data) return null
    const [signed] = await this.sign([toAsset(data as unknown as AssetRow)])
    return signed ?? null
  }

  async getByIds(ids: string[]): Promise<Asset[]> {
    if (ids.length === 0) return []
    const supabase = await this.client()
    const { data, error } = await supabase.from('assets').select(ASSET_COLUMNS).in('id', ids)
    if (error) throw new Error(error.message)
    const found = await this.sign((data ?? []).map((row) => toAsset(row as unknown as AssetRow)))
    // Returned in the order they were asked for: the selection tray and the
    // board both place images in the order the person picked them, and a
    // database is under no obligation to preserve that.
    const byId = new Map(found.map((asset) => [asset.id, asset]))
    return ids.map((id) => byId.get(id)).filter((asset): asset is Asset => Boolean(asset))
  }

  async listTagGroups(library: LibraryKey = 'audit'): Promise<TagGroupDef[]> {
    const supabase = await this.client()
    const { data, error } = await supabase
      .from('tag_groups')
      .select('slug, label, position, hidden')
      .eq('library', library)
      .order('position')
      .order('slug')
    if (error) throw new Error(error.message)
    return ((data ?? []) as unknown as TagGroupDef[]).map((row) => ({
      slug: row.slug,
      label: row.label,
      position: row.position ?? 500,
      hidden: Boolean(row.hidden),
    }))
  }

  async listTags(library: LibraryKey = 'audit'): Promise<Tag[]> {
    const supabase = await this.client()

    // Counted by Postgres, in the tag_counts view. This used to pull the whole
    // asset_tags table into the browser and tally it there — one row per image
    // per tag, on every page load.
    const { data, error } = await supabase
      .from('tag_counts')
      .select('id, slug, name, group:"group", asset_count')
      .eq('library', library)
      .order('name')
    if (error) throw new Error(error.message)

    return ((data ?? []) as unknown as (TagRow & { asset_count: number })[]).map((row) => ({
      ...toTag(row),
      assetCount: row.asset_count ?? 0,
    }))
  }
}
